import React from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { IClientNpsHistoryModel, IClientNpsLivingModel } from 'interfaces';
import {
  DIVIDER_COLOR,
  EmptyDash,
  formatDateDots,
  getNpsColorsOrEmpty,
  LABEL_COLOR,
  SECONDARY_COLOR,
  useCardStyles,
} from './helpers';

interface IProps {
  npsLiving: IClientNpsLivingModel[];
  npsHistory: IClientNpsHistoryModel[];
}

interface INpsRow {
  key: string;
  score: number | null;
  meta: string;
  comment: string | null;
}

const useStyles = makeStyles(() => ({
  body: {
    padding: 16,
  },
  main: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  circle: {
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700,
    flexShrink: 0,
  },
  mainCircle: {
    width: 44,
    height: 44,
    fontSize: 18,
    marginRight: 12,
  },
  meta: {
    fontSize: 12,
    color: LABEL_COLOR,
    marginBottom: 4,
  },
  comment: {
    fontSize: 14,
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  history: {
    marginTop: 14,
    paddingTop: 12,
    borderTop: `1px solid ${DIVIDER_COLOR}`,
  },
  historyFirst: {
    marginTop: 0,
    paddingTop: 0,
    borderTop: 'none',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    fontSize: 13,
    marginTop: 8,
  },
  rowCircle: {
    width: 28,
    height: 28,
    fontSize: 13,
    marginRight: 10,
  },
  rowDate: {
    color: SECONDARY_COLOR,
    minWidth: 84,
    marginRight: 10,
  },
  rowComment: {
    wordBreak: 'break-word',
  },
  rowEmpty: {
    color: LABEL_COLOR,
  },
}));

// «дата · статус · тип опроса», пустые части пропускаем.
const livingMeta = (nps: IClientNpsLivingModel): string =>
  [formatDateDots(nps.insertedAt) || '-', nps.status, nps.surveyType].filter(Boolean).join(' · ');

const livingToRow = (nps: IClientNpsLivingModel): INpsRow => ({
  key: `l-${nps.insertedAt}-${nps.surveyType}-${nps.score}`,
  score: nps.score ?? null,
  meta: livingMeta(nps),
  comment: nps.comment,
});

const historyToRow = (nps: IClientNpsHistoryModel): INpsRow => ({
  key: `h-${nps.date}-${nps.rating}-${nps.comment}`,
  score: nps.rating ?? null,
  meta: formatDateDots(nps.date) || '-',
  comment: nps.comment,
});

/** NPS: последняя оценка живущих крупно, остальные и история оценок — компактными строками. */
const ClientNpsBlock: React.FC<IProps> = ({ npsLiving, npsHistory }) => {
  const classes = useStyles();
  const cardClasses = useCardStyles();
  const [latest, ...restLiving] = npsLiving;
  const rows = restLiving.map(livingToRow).concat(npsHistory.map(historyToRow));

  if (!latest && rows.length === 0) {
    return (
      <Box className={`${cardClasses.card} ${classes.body}`}>
        <EmptyDash />
      </Box>
    );
  }

  const renderLatest = (nps: IClientNpsLivingModel) => {
    const score = nps.score ?? null;
    const colors = getNpsColorsOrEmpty(score);
    const meta = livingMeta(nps);
    return (
      <Box className={classes.main}>
        <Box
          className={`${classes.circle} ${classes.mainCircle}`}
          style={{ backgroundColor: colors.bg, color: colors.color }}
        >
          {score ?? '-'}
        </Box>
        <Box minWidth={0}>
          <Typography className={classes.meta}>{meta}</Typography>
          {nps.comment && <Typography className={classes.comment}>{nps.comment}</Typography>}
        </Box>
      </Box>
    );
  };

  const renderRow = (row: INpsRow) => {
    const colors = getNpsColorsOrEmpty(row.score);
    return (
      <Box className={classes.row} key={row.key}>
        <Box
          className={`${classes.circle} ${classes.rowCircle}`}
          style={{ backgroundColor: colors.bg, color: colors.color }}
        >
          {row.score ?? '-'}
        </Box>
        <span className={classes.rowDate}>{row.meta}</span>
        <span className={row.comment ? classes.rowComment : classes.rowEmpty}>{row.comment || '-'}</span>
      </Box>
    );
  };

  return (
    <Box className={`${cardClasses.card} ${classes.body}`}>
      {latest && renderLatest(latest)}
      <Box className={latest ? classes.history : `${classes.history} ${classes.historyFirst}`}>
        <Typography className={cardClasses.fieldLabel}>История оценок</Typography>
        {rows.length === 0 ? (
          <Box mt={1}>
            <EmptyDash />
          </Box>
        ) : (
          rows.map(renderRow)
        )}
      </Box>
    </Box>
  );
};

export default ClientNpsBlock;
